/**
 * Shared types and constants for the EditCustomersForm component.
 */

import React from 'react';

export const PHONE_TYPES = ['unknown', 'cell', 'home', 'work'] as const;
export type PhoneType = (typeof PHONE_TYPES)[number];

export const CONSENT_FIELDS = ['commercial_sms', 'transactional_sms', 'commercial_call', 'transactional_call'] as const;
export type ConsentField = (typeof CONSENT_FIELDS)[number];

export interface Phone {
  id?: number;
  kind: PhoneType;
  number: string;
  primary: boolean;
  deleted?: boolean;
  tempId: string;
  commercial_sms?: boolean;
  transactional_sms?: boolean;
  commercial_call?: boolean;
  transactional_call?: boolean;
}

export interface Customer {
  id?: number;
  account_id: number;
  name: string;
  email: string;
  primary_email: boolean;
  phones: Phone[];
  tempId: string;
}

export interface Account {
  id: number;
  customers: Customer[];
}

export interface EditCustomersFormProps {
  account: Account;
  onSuccess: () => void;
  onError: (params: { error: { data?: { message?: string } } }) => void;
}

export const CONSENT_INFO: Record<ConsentField, { title: string; content: React.ReactNode }> = {
  commercial_sms: {
    title: 'Commercial SMS',
    content: (
      <ul>
        <li>
          Practically, this permission allows texting leads in order to sell them: to send promotional messages,
          including to sell (or upsell) the customer over SMS.
        </li>
        <li>This permission includes transactional SMS.</li>
        <li>
          If customer has opted out using a &quot;STOP&quot; word text message, you can not override it - customer must
          opt back in. To do this, they must send &quot;UNSTOP&quot; to the same number that was sent the
          &quot;STOP&quot; message. You can see this number by finding the message within the account&apos;s
          &apos;Messages&apos; panel.
        </li>
        <li>
          To obtain permission from this customer for BOTH commercial SMS AND phone, customer must agree to this spoken
          message, in full:
          <div>
            <i>
              I want to confirm if we have your consent to receive service-related and promotional calls or SMS text
              messages, which may be sent using automated technology. Your consent here is in no way a condition of
              making a purchase, and you can unsubscribe at any time by calling this number back. You will continue to
              receive service-related calls or SMS texts. Std. text msg. and data rates may apply. You can check out our
              terms of Service online for more information.
            </i>
          </div>
        </li>
      </ul>
    ),
  },
  transactional_sms: {
    title: 'Transactional SMS',
    content: (
      <ul>
        <li>
          This permission allows us to send service-related texts to customers, including order updates, asking them
          questions about their order, and billing issues.
        </li>
        <li>Most automated text messages fall into this category.</li>
        <li>
          If customer has opted out using a &quot;STOP&quot; word text message, this checkbox will not override it -
          customer must opt back in by sending us an &quot;UNSTOP&quot; text message. To do this, they must send
          &quot;UNSTOP&quot; to the same number that was sent the &quot;STOP&quot; message. You can see this number by
          finding the message within the account&apos;s &apos;Messages&apos; panel.
        </li>
      </ul>
    ),
  },
  commercial_call: {
    title: 'Commercial Call',
    content: (
      <ul>
        <li>
          Practically, this permission allows outbound calling non-converted, non-existing customer leads in order to
          sell them or upsell.
        </li>
        <li>This permission includes transactional calls.</li>
        <li>If we miss a customer&apos;s call we can still call them back.</li>
        <li>
          To obtain permission from this customer for BOTH commercial phone AND SMS, customer must agree to this spoken
          message, in full:
          <div>
            <i>
              I want to confirm if we have your consent to receive service-related and promotional calls or SMS text
              messages, which may be sent using automated technology. Your consent here is in no way a condition of
              making a purchase, and you can unsubscribe at any time by calling this number back. You will continue to
              receive service-related calls or SMS texts. Std. text msg. and data rates may apply. You can check out our
              terms of Service online for more information.
            </i>
          </div>
        </li>
      </ul>
    ),
  },
  transactional_call: {
    title: 'Transactional Call',
    content: (
      <ul>
        <li>
          This permission allows us to call customers for the regular order of business - including order updates and
          questions, following up on billing issues, and inventory updates. Pretty much anything that isn&apos;t an
          attempt to sell the customer.
        </li>
        <li>Trying to re-engage cancelled accounts for a new onboarding is not included.</li>
      </ul>
    ),
  },
};
